/**
 * 系统消息 辅助消息 项组件
 * @author sunzhiguang
 * @date 2020/7/14
 */
import React, { useCallback, useEffect, useState, useContext, useMemo } from "react";
import { connect } from "dva";
import ChatState from "@/types/chat/State";
import Context from "@/components/chat/context";
import GlobalContext, { GlobalContextConfig } from "@/context/GlobalContext";
import { ChatMessage, GroupAttribute, MessageReqType, SessionProps, UID } from "@/types/chat";
import ImService from "@/server/ImService";
import Bus from "@/utils/bus";
import { Modal, Radio, Space, message as toast } from "antd";
import { RadioChangeEvent } from "antd/lib/radio";
import { openUserSelectionModal } from "@/utils/modals";
import { employeeToUid, isEqualEmployee } from "@/utils/chat/message";
import {
  ContactShowType,
  SelectGroupType,
  SelectorMode
} from "@jd/focus-desktop-comps/lib/es/UserSelection";
import get from "lodash/get";

interface SysMessageItemProps {
  message: any;
}

interface DvaProps {
  loadSessionMembers: Function;
  removeSession: Function;
  removeGroupMember: Function;
  updateSessionData: Function;
}

type IProps = Readonly<SysMessageItemProps & ChatState & DvaProps>;

function InviteForwardMsg(props: IProps) {
  const { currentEmployee, userData } = useContext(Context);
  const { appRuntimeEnv } = useContext<GlobalContextConfig>(GlobalContext);
  const { im } = appRuntimeEnv;
  const { message, selectedSession, removeGroupMember } = props;
  const [records, setRecords] = useState<ChatMessage[]>([]);
  const [visible, setVisible] = useState(false);
  const [revokeType, setRevokeType] = useState("");
  const imService = ImService.getInstance();
  const getMessages = useCallback(async () => {
    const mids = message.forwardIdList.map((item: any) => item.mid);
    console.log("mids=>", mids, message.forwardIdList);
    return await imService.getBySessionIdAndMids(message.sessionId, mids);
  }, [imService, message.forwardIdList, message.sessionId]);

  const showRecordsModal = async () => {
    const records = await getMessages();
    const list: ChatMessage[] =
      (records?.sort((a: any, b: any) => a.timestamp - b.timestamp) as ChatMessage[]) || [];
    Bus.emit("chatting-records-modal-show", { modalData: list, canChecked: false });
  };

  const onRadioChange = (e: RadioChangeEvent) => {
    setRevokeType(e.target.value);
  };

  const onCancel = () => {
    setVisible(false);
    setRevokeType("");
  };

  const showRevokeUserModal = (users: UID[]) => {
    const type = revokeType;
    if (!users.length) return;
    const title =
      type === "3"
        ? `确定将${users.map((item: any) => item.name || item.nickName).join("、")}移出群沟通吗`
        : `确定将${users
            .map((item: any) => item.name || item.nickName)
            .join("、")}移出群沟通并撤回分享的沟通记录吗？`;
    Modal.confirm({
      title,
      cancelText: "取消",
      okText: type === "3" ? "移出" : "移出并撤回",
      onOk: async () => {
        if (type !== "3") {
          await handleInviteForwardRevoke(users, true);
        }
        const result = await imService.removeGroupMembers(selectedSession.sessionId, users);
        if (result) {
          await removeGroupMember({ member: users[0] });
        }
      }
    });
  };

  const handleRemoveUsers = async (msgTos: UID[]) => {
    const removeUsers = msgTos.filter((item: any) => item.flag);
    const noAvatarList: any[] = [];
    removeUsers.forEach((item) => {
      noAvatarList.push({
        appId: item.app,
        teamId: item.teamId,
        userId: item.pin
      });
    });
    const detailList = await imService.getChangeUserDetailList(noAvatarList);
    const fixedUsers = removeUsers.map((item: any) => {
      return {
        id: `${item.pin}:${item.app}:${item.teamId}`,
        name: item.name || item.nickName,
        avatar: detailList.find((detail: any) => detail.userId === item.pin)?.avatar || "",
        ...item
      };
    });
    if (removeUsers.length > 1) {
      openUserSelectionModal(
        {
          title: "请选择移出人员",
          frozenIds: [],
          fixedUsers: fixedUsers,
          currentUser: employeeToUid(currentEmployee),
          mode: SelectorMode.group,
          selectedCountText: "已选"
          // mode: SelectorMode.group,
          // contactShowType: ContactShowType.multiTenement,
          // contactsFilterFlag: ["crossPlatform"],
          // selectGroupType: SelectGroupType.innerAndCrossTenement
        },
        (data, close) => {
          const list = get(data, "data.result", []);
          showRevokeUserModal(list);
          onCancel();
          close();
        },
        userData
      );
    } else {
      showRevokeUserModal(removeUsers);
      onCancel();
    }
  };

  const onModalOk = () => {
    const { msgTos = [] } = message;
    const type = revokeType;
    const users = msgTos.filter((user: any) => user.revoke !== 1);
    if (type === "3" || type === "1") {
      handleRemoveUsers(msgTos);
    } else {
      onCancel();
      Modal.confirm({
        title: "确定将分享的沟通记录撤回吗？",
        okText: "撤回",
        cancelText: "取消",
        onOk: () => {
          handleInviteForwardRevoke(users);
        }
      });
    }
  };

  const showRevoke = useMemo(() => {
    if (!message.localSendTimestamp) return false;
    const revoketime = im.chatInviteRevokeTime || 14400000;
    // 解决web端撤销不显示问题
    const nowDate = new Date().getTime() + 200;
    if (nowDate - message.localSendTimestamp <= 0) {
      console.log("当前时间小于消息发送的时间，可能手动改了系统时间");
      return false;
    }
    if (nowDate - message.localSendTimestamp > revoketime) {
      return false;
    }

    return true;
  }, [im.chatInviteRevokeTime, message]);

  const handleInviteForwardRevoke = async (users: UID[], memberDel?: boolean) => {
    const options = {
      msgTos: users,
      subType: MessageReqType.INVITE_FORWARD,
      memberDel
    };
    const result = await imService.revokeMessage(selectedSession, message, 0, options);
    console.log("revoke=>", result);
    return result;
    // options?: {
    //   msgTos: UID[];
    //   subType: MessageReqType;
    // }
  };

  const isMineMsg = useMemo(() => {
    return isEqualEmployee(message.sender, currentEmployee);
  }, [message, currentEmployee]);

  const onInviteWordRevokeClick = () => {
    const revoketime = im.chatInviteRevokeTime || 14400000;
    // 解决web端撤销不显示问题
    const nowDate = new Date().getTime() + 200;
    if (nowDate - message.localSendTimestamp > revoketime) {
      return toast.error("超出撤回时间");
    }
    const { info = {} } = selectedSession;
    const { rosters = {} } = info as any;
    const ids = Object.keys(rosters);
    const { msgTos = [] } = message;
    let flag = false;
    for (let item of msgTos) {
      if (ids.find((id) => id.indexOf(item.pin) > -1)) {
        flag = true;
        item.flag = true;
      } else {
        item.flag = false;
      }
    }
    if (flag) {
      setVisible(true);
    } else {
      Modal.info({
        title: "当前成员已被移出群沟通",
        okText: "我知道了",
        onOk() {}
      });
    }
  };

  return (
    <div className="invite-forward-msg">
      {isMineMsg ? (
        <>
          您邀请{" "}
          {message.msgTos
            ?.map((user: { name: any; nickName: any }) => user.name || user.nickName)
            .join("、")}{" "}
          加入群沟通，并分享了
          <span className="forward-msg-count" onClick={showRecordsModal}>
            {message.forwardCount}条沟通记录
          </span>
          {showRevoke && (
            <span className="invite-forward-revoke-btn" onClick={onInviteWordRevokeClick}>
              撤销
            </span>
          )}
        </>
      ) : (
        <>
          <div className="forward-msg-notice">
            <span className="line" />
            <div className="notice-content">以上为分享的{message.forwardCount}条沟通记录</div>
            <span className="line" />
          </div>
          <div>{message.sender.name}邀请你加入群沟通，并分享了沟通记录</div>
        </>
      )}
      <Modal
        visible={visible}
        title="请选择对应撤销范围操作"
        onCancel={onCancel}
        onOk={onModalOk}
        okText="确定"
        cancelText="取消"
        width={360}
        okButtonProps={{
          disabled: !revokeType
        }}
      >
        <Radio.Group onChange={onRadioChange} value={revokeType}>
          <Space direction="vertical">
            {message.revoke ? (
              <Radio value="3">移除群成员</Radio>
            ) : (
              <>
                <Radio value="1">移出群成员并撤回沟通记录</Radio>
                <Radio value="2">撤回沟通记录</Radio>
              </>
            )}
          </Space>
        </Radio.Group>
      </Modal>
    </div>
  );
}

function mapStateToProps({ chat }: { chat: ChatState }) {
  return {
    selectedSession: chat.selectedSession,
    currentEmployee: chat.currentEmployee
  };
}

function mapDispatchToProps(dispatch: any) {
  return {
    async removeGroupMember(data: { member: UID }) {
      await dispatch({ type: "chat/removeGroupMember", payload: data });
    },
    async loadSessionMembers(data: { selectedSession: SessionProps }) {
      await dispatch({ type: "chat/loadSessionMembers", payload: data });
    },
    async removeSession(data: { sessionId: string }) {
      await dispatch({ type: "chat/removeSession", payload: data });
    },
    async updateSessionData(data: { session: any }) {
      await dispatch({ type: "chat/updateSessionData", payload: data });
    }
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(InviteForwardMsg);
